import React, { useEffect, useState, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Survey } from "survey-react-ui";
import { Model } from "survey-core";
import "survey-core/survey-core.css";
import "../../pages/quizSurvey/AdminDashboard.scss";
import dayjs from "dayjs";
import "./TakeSurvey.scss";
import api from "components/api/api";
import { Spinner } from "reactstrap";
import toast from "react-hot-toast";

function TakeSurveyPage() {
  const { id } = useParams();
  const history = useHistory();
  const [mainTitle, setMainTitle] = useState("");
  const [surveyModel, setSurveyModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);
  const timerRef = useRef(null);
  const [surveyData, setSurveyData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [immediateresultshow, setImmediateResultShow] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const saveIntervalRef = useRef(null);
  const userId = localStorage.getItem("userId") || "anonymous";
  const surveyModelRef = useRef(null);
  const elapsedRef = useRef(0);
  const lastTickRef = useRef(Date.now());
  const totalDurationRef = useRef(0);
  const attemptDataRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleSeeResult = (id, type, title) => {
    localStorage.setItem("Title", title);
    history.replace(`/result/${id}/${type.toLowerCase()}`);
  };

  const saveProgress = async (showToast = false) => {
    if (!surveyModelRef.current?.data || submitting) return;
    try {
      setIsSaving(true);
      await api.saveProgress(id, {
        userId,
        answers: surveyModelRef.current.data,
        currentPageNo: surveyModelRef.current.currentPageNo,
        platform: "WEB",
        client: navigator.userAgent,
        timeSpent: Math.floor(elapsedRef.current),
      });
      if (mountedRef.current && showToast) toast.success("Progress saved!");
    } catch (error) {
      if (mountedRef.current && showToast)
        toast.error("Failed to save progress.");
    } finally {
      if (mountedRef.current) setIsSaving(false);
    }
  };

  const startTimer = (initialElapsed) => {
    if (timerRef.current) clearInterval(timerRef.current);
    elapsedRef.current = initialElapsed || 0;
    lastTickRef.current = Date.now();

    const tick = () => {
      if (!mountedRef.current) return;
      const now = Date.now();
      elapsedRef.current += (now - lastTickRef.current) / 1000;
      lastTickRef.current = now;

      const remaining =
        totalDurationRef.current - Math.floor(elapsedRef.current);

      if (remaining <= 0) {
        clearInterval(timerRef.current);
        if (mountedRef.current) {
          setTimeLeft(0);
          const model = surveyModelRef.current;
          if (model && !model.isCompleted) {
            toast.error("Time's up!");
            model.completeLastPage();
            model.doComplete();
          }
        }
      } else {
        setTimeLeft(remaining);
      }
    };

    tick();
    timerRef.current = setInterval(tick, 1000);
  };

  useEffect(() => {
    const init = async () => {
      try {
        const surveyRes = await api.fetchSurveyById(id, userId);
        if (!surveyRes.data.success) {
          if (mountedRef.current) {
            toast.error("Failed to load quiz.");
            setLoading(false);
          }
          return;
        }
        const data = surveyRes.data.data;
        if (!mountedRef.current) return;
        setSurveyData(data);
        setMainTitle(data.title);
        setImmediateResultShow(data.announcementMode === "IMMEDIATE");

        const attemptRes = await api.startOrResumeAttempt(id, userId);
        const attempt = attemptRes.data.data;
        attemptDataRef.current = attempt;

        if (!mountedRef.current) return;

        if (
          attempt.status === "SUBMITTED" ||
          attempt.status === "AUTO_SUBMITTED"
        ) {
          toast.error("Already submitted.");
          history.push("/quiz-survey");
          return;
        }
        if (attempt.status === "EXPIRED") {
          toast.error("Time has expired. Auto-submitting your answers...");
          if (attempt.answers && Object.keys(attempt.answers).length > 0) {
            try {
              if (mountedRef.current) setSubmitting(true);
              await api.submitSurvey(id, {
                userId,
                answers: attempt.answers,
                finishTime: 0,
              });
              if (mountedRef.current) toast.success("Quiz auto-submitted!");
            } catch (error) {
              if (mountedRef.current) toast.error("Auto-submit failed.");
            } finally {
              if (mountedRef.current) setSubmitting(false);
            }
          }
          history.push("/quiz-survey");
          return;
        }

        totalDurationRef.current = attempt.durationMinutes * 60;
        startTimer(attempt.timeSpent || 0);

        const model = new Model(data.definitionJson);
        model.surveyId = data.id;
        surveyModelRef.current = model;

        model.getAllQuestions().forEach((question) => {
          const originalData = data.definitionJson.pages
            .flatMap((p) => p.elements)
            .find((e) => e.name === question.name);
          if (originalData) {
            if (originalData.arabicTitle) {
              question.title = `${originalData.title}\n${originalData.arabicTitle}`;
            } else {
              question.title = originalData.title;
            }
          }
        });

        model.requiredText = "*";
        model.getAllQuestions().forEach((q) => (q.isRequired = true));

        if (attempt.answers && Object.keys(attempt.answers).length > 0) {
          model.data = attempt.answers;
          if (mountedRef.current) toast.success("Progress restored!");
        }

        model.onComplete.add(async (sender) => {
          clearInterval(timerRef.current);
          clearInterval(saveIntervalRef.current);

          const finishTime = Math.floor(elapsedRef.current);
          try {
            if (mountedRef.current) setSubmitting(true);
            await api.submitSurvey(id, {
              userId,
              answers: sender.data,
              finishTime,
            });
            if (mountedRef.current) {
              toast.success(
                data.type === "Survey"
                  ? "Survey submitted!"
                  : "Quiz submitted!",
              );
            }
            setTimeout(() => {
              if (!mountedRef.current) return;
              if (data.visibilityType === "PRIVATE")
                history.push("/quiz-survey");
              else if (data.announcementMode === "IMMEDIATE")
                handleSeeResult(data.id, data.type, data.title);
              else history.push("/quiz-survey");
            }, 500);
          } catch (error) {
            if (mountedRef.current) toast.error("Submission failed.");
          } finally {
            if (mountedRef.current) setSubmitting(false);
          }
        });

        if (mountedRef.current) {
          setSurveyModel(model);
          setLoading(false);
        }
      } catch (error) {
        console.error(error);
        if (mountedRef.current) {
          toast.error("Error loading quiz.");
          setLoading(false);
        }
      }
    };
    init();

    return () => {
      clearInterval(timerRef.current);
      clearInterval(saveIntervalRef.current);
    };
  }, [id]);

  useEffect(() => {
    if (!loading && surveyModel) {
      saveIntervalRef.current = setInterval(() => saveProgress(), 30000);
      return () => clearInterval(saveIntervalRef.current);
    }
  }, [loading, surveyModel]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        lastTickRef.current = Date.now();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    const handlePopState = (e) => {
      const confirmLeave = window.confirm(
        "Do you want to save your progress before leaving?",
      );
      if (confirmLeave) {
        saveProgress(true);
      }
      history.push(0);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [id, userId]);

  useEffect(() => {
    const sendBeaconSafe = (url, payload) => {
      const blob = new Blob([JSON.stringify(payload)], {
        type: "application/json",
      });
      const sent = navigator.sendBeacon(url, blob);
      if (!sent) {
        try {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", url, false);
          xhr.setRequestHeader("Content-Type", "application/json");
          xhr.send(JSON.stringify(payload));
        } catch (e) {
          console.error("Failed to save on exit:", e);
        }
      }
    };

    const handleBeforeUnload = (e) => {
      if (surveyModelRef.current?.data) {
        const payload = {
          userId,
          answers: surveyModelRef.current.data,
          currentPageNo: surveyModelRef.current.currentPageNo,
          platform: "WEB",
          client: navigator.userAgent,
          timeSpent: Math.floor(elapsedRef.current),
        };
        sendBeaconSafe(
          `${process.env.REACT_APP_API_URL || "http://localhost:8082"}/api/user/attempt/${id}/answers`,
          payload,
        );
      }
      if (elapsedRef.current > 0 && !submitting) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [id, userId, submitting]);

  const formatTime = (s) => {
    if (s === null || s === undefined || s < 0) return "00:00";
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };

  if (loading || submitting) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner
          animation="border"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        />
        {submitting && <p className="ml-3">Submitting...</p>}
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {surveyModel ? (
        <div className="survey-panel">
          <div className="survey-header">
            <h2>{mainTitle}</h2>
            <p className="timer">Time Left: {formatTime(timeLeft)}</p>
          </div>
          <Survey model={surveyModel} />
          <div className="action-buttons">
            <button
              className="save-btn"
              onClick={() => saveProgress(true)}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Progress"}
            </button>
            <button
              className="back-btn"
              onClick={() => {
                if (window.confirm("Save progress before leaving?")) {
                  saveProgress(true).then(() => history.push("/quiz-survey"));
                } else {
                  history.push("/quiz-survey");
                }
              }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      ) : (
        <p>Quiz not found.</p>
      )}
    </div>
  );
}

export default TakeSurveyPage;
